import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { pickAndUploadImage } from "../lib/upload";
import {
  Plus, Trash2, Copy, Search, ArrowLeft, Info, ListChecks, CalendarDays,
  Images, FileText, CreditCard, MessageSquare, Send, ClipboardCheck, Activity, ChevronDown, ChevronUp,
} from "lucide-react";

const errText = (d, f) => (typeof d === "string" ? d : f);
const money = (n) => `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

async function uploadImageOrFail(file) {
  try { return await pickAndUploadImage(file); }
  catch (err) { toast.error(err?.response?.data?.detail || err.message || "Upload failed"); return null; }
}
async function uploadFileOrFail(file) {
  const form = new FormData();
  form.append("file", file);
  try {
    const { data } = await api.post("/admin/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
    const base = process.env.REACT_APP_BACKEND_URL || "";
    return data.url.startsWith("http") ? data.url : `${base}${data.url}`;
  } catch (err) {
    toast.error(err?.response?.data?.detail || err.message || "Upload failed");
    return null;
  }
}

const Field = (props) => <input {...props} className={`editorial-input ${props.className || ""}`} />;
const Sel = ({ children, ...p }) => <select {...p} className="editorial-input [&>option]:bg-black">{children}</select>;
const AddBtn = ({ children, ...p }) => <button {...p} className="w-full bg-white/10 hover:bg-gold hover:text-black text-white text-xs uppercase tracking-widest py-2.5 transition-colors flex items-center justify-center gap-2"><Plus size={14} />{children}</button>;
const Row = ({ children, onDelete, testid }) => (
  <div data-testid={testid} className="flex items-center justify-between gap-3 border border-white/10 px-4 py-3 text-sm">
    <div className="min-w-0">{children}</div>
    {onDelete && <button onClick={onDelete} className="text-white/40 hover:text-red-400 shrink-0"><Trash2 size={15} /></button>}
  </div>
);

/* ---------- Manage a single project ---------- */
const TABS = [
  { k: "info", l: "Info", icon: Info },
  { k: "timeline", l: "Timeline", icon: ListChecks },
  { k: "schedule", l: "Schedule", icon: CalendarDays },
  { k: "gallery", l: "Gallery", icon: Images },
  { k: "documents", l: "Documents", icon: FileText },
  { k: "payments", l: "Payments", icon: CreditCard },
  { k: "inspections", l: "Inspections", icon: ClipboardCheck },
  { k: "messages", l: "Messages", icon: MessageSquare },
];

function ClientActivityCard({ job }) {
  const [expanded, setExpanded] = useState(false);
  const logins = job.portal_logins || [];
  const count = job.portal_login_count || 0;
  const lastAt = job.portal_last_login_at || null;
  const fmt = (iso) => new Date(iso).toLocaleString("en-US", {
    month: "short", day: "numeric", year: "numeric",
    hour: "numeric", minute: "2-digit",
  });
  const deviceHint = (ua) => {
    if (!ua) return "Unknown device";
    if (/iPhone|iPod/i.test(ua)) return "iPhone";
    if (/Android/i.test(ua)) return "Android";
    if (/iPad/i.test(ua)) return "iPad";
    if (/Mac OS X/i.test(ua)) return "Mac";
    if (/Windows/i.test(ua)) return "Windows PC";
    return "Web";
  };
  return (
    <div data-testid="client-activity-card" className="mb-6 border border-white/10 bg-[#0a0a0a] p-4">
      <div className="flex flex-wrap items-center gap-4 justify-between">
        <div className="flex items-center gap-4">
          <Activity size={22} className="text-gold shrink-0" strokeWidth={1.5} />
          <div>
            <p className="text-[10px] uppercase tracking-widest text-gold">Client Portal Activity</p>
            <p className="font-body text-white mt-1">
              <span className="font-serif text-2xl text-gold" data-testid="portal-login-count">{count}</span>
              <span className="text-white/50 text-sm ml-2">total logins</span>
              {lastAt && <span className="text-white/50 text-sm ml-4">· Last visit <span className="text-white">{fmt(lastAt)}</span></span>}
              {!count && <span className="text-white/40 text-sm ml-2">— client hasn't entered the portal yet</span>}
            </p>
          </div>
        </div>
        {logins.length > 0 && (
          <button
            onClick={() => setExpanded(!expanded)}
            data-testid="portal-activity-toggle"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-white/60 hover:text-gold border border-white/15 px-3 py-1.5"
          >
            {expanded ? "Hide history" : `Show history (${logins.length})`}
            {expanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
          </button>
        )}
      </div>
      {expanded && logins.length > 0 && (
        <div className="mt-4 border-t border-white/10 pt-4 space-y-2 max-h-64 overflow-y-auto">
          {[...logins].reverse().map((l, i) => (
            <div key={i} className="flex flex-wrap items-center justify-between gap-3 text-sm py-1.5 border-b border-white/5 last:border-b-0">
              <div className="flex items-center gap-3">
                <span className="text-white/40 text-xs uppercase tracking-widest w-16">#{logins.length - i}</span>
                <span className="font-body text-white">{fmt(l.at)}</span>
              </div>
              <div className="flex items-center gap-3 text-xs">
                <span className="text-white/60">{deviceHint(l.ua)}</span>
                {l.ip && <span className="text-white/30 font-mono">{l.ip}</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Manage({ jobId, clients, onBack, onChanged }) {
  const [job, setJob] = useState(null);
  const [tab, setTab] = useState("info");
  const refresh = async () => { const { data } = await api.get(`/admin/jobs`); const j = data.find((x) => x.id === jobId); setJob(j); onChanged && onChanged(); };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [jobId]);

  if (!job) return <p className="text-white/40 p-6">Loading…</p>;

  const patch = (updated) => setJob(updated);
  const put = async (body) => { const { data } = await api.put(`/admin/jobs/${jobId}`, body); patch(data); onChanged && onChanged(); toast.success("Saved"); };
  const paid = (job.payments || []).filter((p) => p.status === "paid").reduce((s, p) => s + Number(p.amount || 0), 0);

  return (
    <div>
      <button onClick={onBack} className="inline-flex items-center gap-2 text-white/60 hover:text-gold text-sm mb-5"><ArrowLeft size={15} /> All projects</button>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-2">
        <h2 className="font-serif text-3xl">{job.title}</h2>
        <button onClick={() => { navigator.clipboard?.writeText(job.invitation_code); toast.success("Code copied"); }} className="inline-flex items-center gap-2 border border-gold/40 text-gold px-3 py-1.5 text-xs uppercase tracking-widest hover:bg-gold hover:text-black transition-colors"><Copy size={13} /> {job.invitation_code}</button>
      </div>
      <p className="text-white/40 text-sm mb-6">{job.client_name} · {job.location || "—"}</p>

      {typeof job.satisfaction_rating === "number" && (
        <div data-testid="admin-satisfaction-banner" className="mb-6 border border-gold/25 bg-gradient-to-r from-[#0d0b06] to-[#0a0a0a] p-4 flex flex-wrap items-center gap-4 justify-between">
          <div className="flex items-center gap-4">
            <div>
              <p className="text-[10px] uppercase tracking-widest text-gold">Client Satisfaction</p>
              <p className="font-serif text-3xl mt-1">{job.satisfaction_rating}<span className="text-white/30 text-lg">/100</span></p>
            </div>
            {job.satisfaction_comment && (
              <p className="text-white/70 text-sm italic max-w-md border-l border-white/15 pl-4">"{job.satisfaction_comment}"</p>
            )}
          </div>
          {job.satisfaction_updated_at && (
            <p className="text-white/40 text-xs">
              Updated {new Date(job.satisfaction_updated_at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
            </p>
          )}
        </div>
      )}

      <ClientActivityCard job={job} />

      <div className="flex gap-1 overflow-x-auto border-b border-white/10 mb-6">
        {TABS.map((t) => { const Icon = t.icon; return (
          <button key={t.k} data-testid={`mtab-${t.k}`} onClick={() => setTab(t.k)} className={`flex items-center gap-2 px-4 py-3 text-xs uppercase tracking-widest whitespace-nowrap border-b-2 -mb-px transition-colors ${tab === t.k ? "border-gold text-gold" : "border-transparent text-white/50 hover:text-white"}`}><Icon size={14} /> {t.l}</button>
        ); })}
      </div>

      {tab === "info" && <InfoTab job={job} clients={clients} put={put} />}
      {tab === "timeline" && <TimelineTab job={job} patch={patch} onChanged={onChanged} />}
      {tab === "schedule" && <ScheduleTab job={job} patch={patch} />}
      {tab === "gallery" && <GalleryTab job={job} patch={patch} />}
      {tab === "documents" && <DocsTab job={job} patch={patch} />}
      {tab === "payments" && <PaymentsTab job={job} patch={patch} paid={paid} />}
      {tab === "inspections" && <InspectionsTab job={job} patch={patch} />}
      {tab === "messages" && <MessagesTab job={job} patch={patch} />}
    </div>
  );
}

function InfoTab({ job, clients, put }) {
  const [progress, setProgress] = useState(job.progress || 0);
  const [status, setStatus] = useState(job.status);
  const [total, setTotal] = useState(job.contract_total || 0);
  const [clientId, setClientId] = useState(job.client_user_id || "");
  return (
    <div className="max-w-lg space-y-6">
      <div>
        <label className="text-xs uppercase tracking-widest text-white/50">Progress %</label>
        <input type="range" min={0} max={100} value={progress} onChange={(e) => setProgress(Number(e.target.value))} className="w-full accent-[#d4af37]" data-testid="info-progress" />
        <p className="text-gold font-serif text-2xl">{progress}%</p>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div><label className="text-xs uppercase tracking-widest text-white/50">Status</label>
          <Sel value={status} onChange={(e) => setStatus(e.target.value)} data-testid="info-status">
            <option value="in_progress">In Progress</option><option value="on_hold">On Hold</option><option value="completed">Completed</option>
          </Sel></div>
        <div><label className="text-xs uppercase tracking-widest text-white/50">Contract Total ($)</label>
          <Field type="number" value={total} onChange={(e) => setTotal(e.target.value)} data-testid="info-total" /></div>
      </div>
      <div>
        <label className="text-xs uppercase tracking-widest text-white/50">Assigned Client</label>
        <Sel value={clientId} onChange={(e) => setClientId(e.target.value)} data-testid="info-client">
          <option value="">— Unassigned (client can self-register with code) —</option>
          {clients.map((c) => <option key={c.id} value={c.id}>{c.name} ({c.email})</option>)}
        </Sel>
      </div>
      <button data-testid="info-save" onClick={() => put({ progress: Number(progress), status, contract_total: Number(total), client_user_id: clientId || null })} className="bg-gold text-black text-xs uppercase tracking-widest px-6 py-3 hover:bg-white transition-colors">Save changes</button>
    </div>
  );
}

function TimelineTab({ job, patch, onChanged }) {
  const [s, setS] = useState({ title: "", description: "", date: "", status: "pending", video_url: "" });
  const [photos, setPhotos] = useState([]);
  const add = async (e) => {
    e.preventDefault(); if (!s.title) return;
    const { data } = await api.post(`/admin/jobs/${job.id}/stages`, { ...s, photos });
    patch(data); onChanged && onChanged(); setS({ title: "", description: "", date: "", status: "pending", video_url: "" }); setPhotos([]); toast.success("Stage added");
  };
  const del = async (id) => { const { data } = await api.delete(`/admin/jobs/${job.id}/stages/${id}`); patch(data); };
  return (
    <div className="grid lg:grid-cols-2 gap-8">
      <div className="space-y-2">
        {(job.stages || []).map((st) => <Row key={st.id} testid={`stage-row-${st.id}`} onDelete={() => del(st.id)}><span className="text-white">{st.title}</span> <span className="text-white/40">· {st.status} {st.date && `· ${st.date}`}</span></Row>)}
        {!(job.stages || []).length && <p className="text-white/40 text-sm">No stages yet.</p>}
      </div>
      <form onSubmit={add} className="border border-white/10 p-5 space-y-3 h-fit" data-testid="stage-form">
        <p className="text-xs uppercase tracking-widest text-gold">New stage</p>
        <Field required placeholder="Title" value={s.title} onChange={(e) => setS({ ...s, title: e.target.value })} data-testid="stage-title" />
        <textarea placeholder="Description" rows={2} value={s.description} onChange={(e) => setS({ ...s, description: e.target.value })} className="editorial-input resize-none" />
        <div className="grid grid-cols-2 gap-3">
          <Field type="date" value={s.date} onChange={(e) => setS({ ...s, date: e.target.value })} className="[color-scheme:dark]" />
          <Sel value={s.status} onChange={(e) => setS({ ...s, status: e.target.value })}><option value="pending">Pending</option><option value="in_progress">In progress</option><option value="completed">Completed</option></Sel>
        </div>
        <Field placeholder="Video URL (optional)" value={s.video_url} onChange={(e) => setS({ ...s, video_url: e.target.value })} />
        <input type="file" accept="image/*" multiple onChange={async (e) => {
          const files = Array.from(e.target.files || []);
          e.target.value = "";
          const results = await Promise.all(files.map(uploadImageOrFail));
          setPhotos(results.filter(Boolean));
        }} className="text-white/60 text-sm" />
        {photos.length > 0 && <p className="text-xs text-gold">{photos.length} photo(s)</p>}
        <AddBtn data-testid="stage-submit">Add stage</AddBtn>
      </form>
    </div>
  );
}

function ScheduleTab({ job, patch }) {
  const [ev, setEv] = useState({ title: "", date: "", note: "" });
  const add = async (e) => { e.preventDefault(); if (!ev.title || !ev.date) return; const { data } = await api.post(`/admin/jobs/${job.id}/events`, ev); patch(data); setEv({ title: "", date: "", note: "" }); toast.success("Date added"); };
  const del = async (id) => { const { data } = await api.delete(`/admin/jobs/${job.id}/events/${id}`); patch(data); };
  return (
    <div className="grid lg:grid-cols-2 gap-8">
      <div className="space-y-2">
        {(job.events || []).map((e) => <Row key={e.id} testid={`event-row-${e.id}`} onDelete={() => del(e.id)}><span className="text-white">{e.title}</span> <span className="text-white/40">· {e.date} {e.note && `· ${e.note}`}</span></Row>)}
        {!(job.events || []).length && <p className="text-white/40 text-sm">No dates yet.</p>}
      </div>
      <form onSubmit={add} className="border border-white/10 p-5 space-y-3 h-fit" data-testid="event-form">
        <p className="text-xs uppercase tracking-widest text-gold">New work date</p>
        <Field required placeholder="Title (e.g. Cabinet install)" value={ev.title} onChange={(e) => setEv({ ...ev, title: e.target.value })} data-testid="event-title" />
        <Field required type="date" value={ev.date} onChange={(e) => setEv({ ...ev, date: e.target.value })} className="[color-scheme:dark]" data-testid="event-date" />
        <Field placeholder="Note (optional)" value={ev.note} onChange={(e) => setEv({ ...ev, note: e.target.value })} />
        <AddBtn data-testid="event-submit">Add date</AddBtn>
      </form>
    </div>
  );
}

function GalleryTab({ job, patch }) {
  const [ph, setPh] = useState({ phase: "before", url: "", caption: "" });
  const add = async (e) => {
    e.preventDefault(); if (!ph.url) return toast.error("Add an image URL or file");
    const { data } = await api.post(`/admin/jobs/${job.id}/photos`, ph); patch(data); setPh({ phase: ph.phase, url: "", caption: "" }); toast.success("Photo added");
  };
  const del = async (id) => { const { data } = await api.delete(`/admin/jobs/${job.id}/photos/${id}`); patch(data); };
  const phases = ["before", "during", "after"];
  return (
    <div className="grid lg:grid-cols-2 gap-8">
      <div className="space-y-4">
        {phases.map((p) => {
          const items = (job.photos || []).filter((x) => x.phase === p);
          return (
            <div key={p}>
              <p className="text-xs uppercase tracking-widest text-gold mb-2">{p} ({items.length})</p>
              <div className="grid grid-cols-3 gap-2">
                {items.map((x) => (
                  <div key={x.id} className="relative group border border-white/10 aspect-square overflow-hidden" data-testid={`photo-${x.id}`}>
                    <img src={x.url} alt="" className="h-full w-full object-cover" />
                    <button onClick={() => del(x.id)} className="absolute top-1 right-1 bg-black/70 p-1 text-white/70 hover:text-red-400 opacity-0 group-hover:opacity-100"><Trash2 size={13} /></button>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      <form onSubmit={add} className="border border-white/10 p-5 space-y-3 h-fit" data-testid="photo-form">
        <p className="text-xs uppercase tracking-widest text-gold">Add photo</p>
        <Sel value={ph.phase} onChange={(e) => setPh({ ...ph, phase: e.target.value })} data-testid="photo-phase"><option value="before">Before</option><option value="during">During</option><option value="after">After</option></Sel>
        <input type="file" accept="image/*" onChange={async (e) => {
          const f = e.target.files?.[0]; e.target.value = "";
          if (!f) return;
          const url = await uploadImageOrFail(f);
          if (url) setPh({ ...ph, url });
        }} className="text-white/60 text-sm block" data-testid="photo-file" />
        <Field placeholder="…or paste image URL" value={ph.url} onChange={(e) => setPh({ ...ph, url: e.target.value })} data-testid="photo-url" />
        <Field placeholder="Caption (optional)" value={ph.caption} onChange={(e) => setPh({ ...ph, caption: e.target.value })} />
        {ph.url && <img src={ph.url} alt="" className="h-24 w-full object-cover border border-white/10" />}
        <AddBtn data-testid="photo-submit">Add photo</AddBtn>
      </form>
    </div>
  );
}

function DocsTab({ job, patch }) {
  const [d, setD] = useState({ name: "", category: "contract", url: "" });
  const add = async (e) => { e.preventDefault(); if (!d.name || !d.url) return toast.error("Name and file/URL required"); const { data } = await api.post(`/admin/jobs/${job.id}/documents`, d); patch(data); setD({ name: "", category: "contract", url: "" }); toast.success("Document added"); };
  const del = async (id) => { const { data } = await api.delete(`/admin/jobs/${job.id}/documents/${id}`); patch(data); };
  return (
    <div className="grid lg:grid-cols-2 gap-8">
      <div className="space-y-2">
        {(job.documents || []).map((x) => <Row key={x.id} testid={`doc-row-${x.id}`} onDelete={() => del(x.id)}><span className="text-white">{x.name}</span> <span className="text-white/40">· {x.category}</span></Row>)}
        {!(job.documents || []).length && <p className="text-white/40 text-sm">No documents yet.</p>}
      </div>
      <form onSubmit={add} className="border border-white/10 p-5 space-y-3 h-fit" data-testid="doc-form">
        <p className="text-xs uppercase tracking-widest text-gold">Add document</p>
        <Field required placeholder="Document name" value={d.name} onChange={(e) => setD({ ...d, name: e.target.value })} data-testid="doc-name" />
        <Sel value={d.category} onChange={(e) => setD({ ...d, category: e.target.value })} data-testid="doc-category"><option value="contract">Contract</option><option value="proposal">Proposal</option><option value="warranty">Warranty</option><option value="invoice">Invoice</option></Sel>
        <input type="file" onChange={async (e) => {
          const f = e.target.files?.[0]; e.target.value = "";
          if (!f) return;
          const url = await uploadFileOrFail(f);
          if (url) setD({ ...d, url, name: d.name || f.name });
        }} className="text-white/60 text-sm block" data-testid="doc-file" />
        <Field placeholder="…or paste document URL" value={d.url} onChange={(e) => setD({ ...d, url: e.target.value })} data-testid="doc-url" />
        <AddBtn data-testid="doc-submit">Add document</AddBtn>
      </form>
    </div>
  );
}

function PaymentsTab({ job, patch, paid }) {
  const [pm, setPm] = useState({ label: "", amount: "", status: "paid", date: "" });
  const add = async (e) => { e.preventDefault(); if (!pm.label || !pm.amount) return; const { data } = await api.post(`/admin/jobs/${job.id}/payments`, { ...pm, amount: Number(pm.amount) }); patch(data); setPm({ label: "", amount: "", status: "paid", date: "" }); toast.success("Payment added"); };
  const del = async (id) => { const { data } = await api.delete(`/admin/jobs/${job.id}/payments/${id}`); patch(data); };
  const balance = Math.max(Number(job.contract_total || 0) - paid, 0);
  return (
    <div className="grid lg:grid-cols-2 gap-8">
      <div>
        <div className="grid grid-cols-3 gap-3 mb-4 text-center">
          <div className="border border-white/10 p-3"><p className="text-[10px] uppercase text-white/40">Total</p><p className="font-serif text-lg">{money(job.contract_total)}</p></div>
          <div className="border border-white/10 p-3"><p className="text-[10px] uppercase text-white/40">Paid</p><p className="font-serif text-lg text-gold">{money(paid)}</p></div>
          <div className="border border-white/10 p-3"><p className="text-[10px] uppercase text-white/40">Balance</p><p className="font-serif text-lg">{money(balance)}</p></div>
        </div>
        <div className="space-y-2">
          {(job.payments || []).map((x) => <Row key={x.id} testid={`payment-row-${x.id}`} onDelete={() => del(x.id)}><span className="text-white">{x.label}</span> <span className="text-white/40">· {money(x.amount)} · {x.status}</span></Row>)}
          {!(job.payments || []).length && <p className="text-white/40 text-sm">No payments yet.</p>}
        </div>
      </div>
      <form onSubmit={add} className="border border-white/10 p-5 space-y-3 h-fit" data-testid="payment-form">
        <p className="text-xs uppercase tracking-widest text-gold">Record payment</p>
        <Field required placeholder="Label (e.g. Deposit)" value={pm.label} onChange={(e) => setPm({ ...pm, label: e.target.value })} data-testid="payment-label" />
        <div className="grid grid-cols-2 gap-3">
          <Field required type="number" placeholder="Amount" value={pm.amount} onChange={(e) => setPm({ ...pm, amount: e.target.value })} data-testid="payment-amount" />
          <Sel value={pm.status} onChange={(e) => setPm({ ...pm, status: e.target.value })} data-testid="payment-status"><option value="paid">Paid</option><option value="pending">Pending</option></Sel>
        </div>
        <Field type="date" value={pm.date} onChange={(e) => setPm({ ...pm, date: e.target.value })} className="[color-scheme:dark]" />
        <AddBtn data-testid="payment-submit">Add payment</AddBtn>
      </form>
    </div>
  );
}

function InspectionsTab({ job, patch }) {
  const [i, setI] = useState({ title: "", date: "", notes: "", status: "scheduled" });
  const add = async (e) => { e.preventDefault(); if (!i.title) return; const { data } = await api.post(`/admin/jobs/${job.id}/inspections`, i); patch(data); setI({ title: "", date: "", notes: "", status: "scheduled" }); toast.success("Inspection added"); };
  const del = async (id) => { const { data } = await api.delete(`/admin/jobs/${job.id}/inspections/${id}`); patch(data); };
  return (
    <div className="grid lg:grid-cols-2 gap-8">
      <div className="space-y-2">
        {(job.inspections || []).map((x) => <Row key={x.id} testid={`insp-row-${x.id}`} onDelete={() => del(x.id)}><span className="text-white">{x.title}</span> <span className="text-white/40">· {x.date || "—"} · {x.status}</span></Row>)}
        {!(job.inspections || []).length && <p className="text-white/40 text-sm">No inspections yet.</p>}
      </div>
      <form onSubmit={add} className="border border-white/10 p-5 space-y-3 h-fit" data-testid="insp-form">
        <p className="text-xs uppercase tracking-widest text-gold">New inspection</p>
        <Field required placeholder="Title" value={i.title} onChange={(e) => setI({ ...i, title: e.target.value })} data-testid="insp-title" />
        <div className="grid grid-cols-2 gap-3">
          <Field type="date" value={i.date} onChange={(e) => setI({ ...i, date: e.target.value })} className="[color-scheme:dark]" />
          <Sel value={i.status} onChange={(e) => setI({ ...i, status: e.target.value })}><option value="scheduled">Scheduled</option><option value="done">Done</option></Sel>
        </div>
        <Field placeholder="Notes" value={i.notes} onChange={(e) => setI({ ...i, notes: e.target.value })} />
        <AddBtn data-testid="insp-submit">Add inspection</AddBtn>
      </form>
    </div>
  );
}

function MessagesTab({ job, patch }) {
  const [text, setText] = useState("");
  const send = async (e) => { e.preventDefault(); if (!text.trim()) return; const { data } = await api.post(`/admin/jobs/${job.id}/messages`, { text }); patch(data); setText(""); };
  return (
    <div className="max-w-2xl">
      <div className="border border-white/10 bg-[#0a0a0a] p-5 space-y-3 max-h-[45vh] overflow-y-auto min-h-[200px]">
        {(job.messages || []).map((m) => (
          <div key={m.id} className={`flex ${m.sender === "company" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[75%] p-3 ${m.sender === "company" ? "bg-gold text-black" : "bg-white/5 border border-white/10 text-white"}`}>
              <p className="text-[10px] uppercase tracking-widest mb-1 opacity-70">{m.sender === "company" ? "GI Remodeling" : "Client"}</p>
              <p className="text-sm whitespace-pre-wrap">{m.text}</p>
            </div>
          </div>
        ))}
        {!(job.messages || []).length && <p className="text-white/40 text-sm">No messages yet.</p>}
      </div>
      <form onSubmit={send} className="flex gap-3 mt-4">
        <Field placeholder="Reply to client…" value={text} onChange={(e) => setText(e.target.value)} data-testid="admin-msg-input" className="flex-1" />
        <button className="bg-gold text-black px-6 hover:bg-white transition-colors flex items-center gap-2 text-xs uppercase tracking-widest" data-testid="admin-msg-send"><Send size={14} /> Send</button>
      </form>
    </div>
  );
}

/* ---------- Projects list + create ---------- */
export default function AdminJobs() {
  const [jobs, setJobs] = useState([]);
  const [clients, setClients] = useState([]);
  const [manageId, setManageId] = useState(null);
  const [q, setQ] = useState("");
  const [statusF, setStatusF] = useState("all");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: "", client_name: "", location: "", description: "" });
  const [cover, setCover] = useState("");
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const load = () => api.get("/admin/jobs").then((r) => setJobs(r.data)).catch(() => {});
  const loadClients = () => api.get("/admin/clients").then((r) => setClients(r.data)).catch(() => {});
  useEffect(() => { load(); loadClients(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try { await api.post("/admin/jobs", { ...form, cover_image: cover }); toast.success("Project created"); setForm({ title: "", client_name: "", location: "", description: "" }); setCover(""); setShowForm(false); load(); }
    catch (err) { toast.error(errText(err.response?.data?.detail, "Error")); }
  };
  const del = async (id) => { if (!window.confirm("Delete this project?")) return; await api.delete(`/admin/jobs/${id}`); toast.success("Project deleted"); load(); };

  const filtered = useMemo(() => jobs.filter((j) =>
    (statusF === "all" || j.status === statusF) &&
    (j.title + " " + j.client_name + " " + (j.location || "")).toLowerCase().includes(q.toLowerCase())
  ), [jobs, q, statusF]);

  if (manageId) return <Manage jobId={manageId} clients={clients} onBack={() => setManageId(null)} onChanged={load} />;

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <h2 className="font-serif text-3xl md:text-4xl">Projects</h2>
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
            <input data-testid="project-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="bg-transparent border border-white/15 pl-9 pr-3 py-2 text-sm text-white focus:border-gold outline-none" />
          </div>
          <select data-testid="project-status-filter" value={statusF} onChange={(e) => setStatusF(e.target.value)} className="bg-transparent border border-white/15 px-3 py-2 text-sm text-white [&>option]:bg-black">
            <option value="all">All statuses</option><option value="in_progress">In Progress</option><option value="on_hold">On Hold</option><option value="completed">Completed</option>
          </select>
          <button data-testid="new-project-btn" onClick={() => setShowForm(true)} className="inline-flex items-center gap-2 bg-gold text-black text-xs uppercase tracking-widest px-5 py-2.5 hover:bg-white transition-colors"><Plus size={15} /> New Project</button>
        </div>
      </div>

      <div className="border border-white/10 divide-y divide-white/10">
        {filtered.map((j) => (
          <div key={j.id} role="button" tabIndex={0} data-testid={`project-row-${j.id}`} onClick={() => setManageId(j.id)} onKeyDown={(e) => { if (e.key === "Enter") setManageId(j.id); }} className="w-full flex items-center justify-between gap-4 p-5 text-left hover:bg-white/[0.02] cursor-pointer">
            <div className="min-w-0">
              <p className="font-serif text-xl">{j.title}</p>
              <p className="text-white/40 text-sm">{j.client_name} · {j.location || "—"} · <span className="text-gold">{j.invitation_code}</span></p>
            </div>
            <div className="flex items-center gap-4 shrink-0">
              <span className="text-[10px] uppercase tracking-widest border border-white/15 px-2 py-1 text-white/60">{j.status}</span>
              <div className="w-28 h-1.5 bg-white/10 hidden sm:block"><div className="h-full bg-gold" style={{ width: `${j.progress || 0}%` }} /></div>
              <span className="text-xs text-gold w-9 text-right">{j.progress || 0}%</span>
              <button onClick={(e) => { e.stopPropagation(); del(j.id); }} data-testid={`delete-project-${j.id}`} className="text-white/40 hover:text-red-400"><Trash2 size={16} /></button>
            </div>
          </div>
        ))}
        {!filtered.length && <p className="text-white/40 font-body p-6">No projects found.</p>}
      </div>

      {showForm && (
        <div className="fixed inset-0 z-[200] bg-black/80 backdrop-blur-sm flex items-center justify-center p-6" onClick={() => setShowForm(false)}>
          <div className="w-full max-w-md border border-white/10 bg-[#0a0a0a] p-8" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-serif text-2xl mb-6">New Project</h3>
            <form onSubmit={create} className="space-y-4" data-testid="job-form">
              <Field required placeholder="Project title" value={form.title} onChange={set("title")} data-testid="job-title" />
              <Field required placeholder="Client name" value={form.client_name} onChange={set("client_name")} data-testid="job-client" />
              <Field placeholder="Location" value={form.location} onChange={set("location")} data-testid="job-location" />
              <textarea rows={2} placeholder="Description" value={form.description} onChange={set("description")} className="editorial-input resize-none" />
              <input type="file" accept="image/*" onChange={async (e) => {
                const f = e.target.files?.[0]; e.target.value = "";
                if (!f) return;
                const url = await uploadImageOrFail(f);
                if (url) setCover(url);
              }} className="text-white/60 text-sm block" data-testid="job-cover" />
              {cover && <img src={cover} alt="" className="h-24 w-full object-cover border border-white/10" />}
              <button className="w-full bg-gold text-black text-xs uppercase tracking-widest py-3 hover:bg-white transition-colors" data-testid="job-submit">Create project</button>
              <p className="text-white/40 text-xs">A unique invitation code is generated. Assign a client or share the code so they can register.</p>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
